import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { Loader2, CheckCircle, ArrowRight, Map, Flag, TrendingUp, Zap, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import InteractiveRoadmapNode from './InteractiveRoadmapNode';
import WhatIfPegman from './WhatIfPegman';
import WhatIfPopover from './WhatIfPopover';
import { getResolvedNodes, isNodeOverridden, isOverrideStartNode, getOriginalNodeAt } from '../utils/whatIfHelpers';
import { toast } from 'sonner';
import './RoadmapVisualization.css';

// Helper function to clean seniority levels from titles
const getCleanTitle = (title) => title ? title.split(/ at /i)[0].trim() : '';

// Helper to ensure nodes have years with proper 1-year increments
const ensureNodeYears = (nodes) => {
  if (!nodes || nodes.length === 0) return nodes;

  const currentYear = new Date().getFullYear();

  return nodes.map((node, index) => {
    // If node already has a valid year, check if gap makes sense
    if (node.year && typeof node.year === 'number') {
      return node;
    }

    // Calculate year based on index (1-year increments from current year)
    const calculatedYear = currentYear + index;

    return {
      ...node,
      year: calculatedYear
    };
  });
};

// Animated timeline connector component
const TimelineConnector = ({ softColor, index, isAnimating = true }) => {
  return (
    <div className="timeline-connector-wrapper flex flex-col items-center md:items-center -my-4 relative z-0" style={{ transform: 'translateZ(0)' }}>
      {/* Animated line */}
      <motion.div
        initial={isAnimating ? { height: 0, opacity: 0 } : { height: 48, opacity: 1 }}
        animate={{ height: 48, opacity: 1 }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className="relative"
        style={{ width: 4 }}
      >
        {/* Background line */}
        <div
          className="absolute inset-0 rounded-full"
          style={{
            backgroundColor: `${softColor}30`,
            width: 4
          }}
        />
        {/* Animated fill */}
        <motion.div
          initial={isAnimating ? { height: '0%' } : { height: '100%' }}
          animate={{ height: '100%' }}
          transition={{ duration: 0.6, delay: index * 0.15, ease: "easeOut" }}
          className="absolute top-0 left-0 right-0 rounded-full"
          style={{
            backgroundColor: softColor,
            width: 4
          }}
        />
      </motion.div>

      {/* Year gap indicator */}
      <motion.div
        initial={isAnimating ? { opacity: 0, x: -10 } : { opacity: 1, x: 0 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.3, delay: index * 0.15 + 0.3 }}
        className="absolute left-1/2 top-1/2 -translate-y-1/2 ml-4 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
        style={{
          backgroundColor: `${softColor}20`,
          color: softColor,
          border: `1px solid ${softColor}40`
        }}
      >
        +1 yr
      </motion.div>
    </div>
  );
};

const RoadmapVisualization = ({ path, color, riskLevel, roadmapId, formData, onRetry, isGenerating, onSetRoadmap, onDislikeRole, savedRoles, onSaveRole, whatIfOverrides, whatIfActiveNode, whatIfLoading, onWhatIfDrop, onWhatIfSubmit, onWhatIfReset, onWhatIfClose, hasLifetimeAccess, onPaywallRequired, onApplyWithTsenta }) => {
  const navigate = useNavigate();
  const [selectedNode, setSelectedNode] = useState(null);
  const bottomRef = useRef(null);
  
  // --- BUILDER STATE ---
  // Tracks the step-by-step constructed path
  const [userNodes, setUserNodes] = useState([]);
  // Tracks the current set of floating bubble options for the NEXT step
  const [nextOptions, setNextOptions] = useState([]);
  // Controls when the path is "locked" and comparisons are generated
  const [isFrozen, setIsFrozen] = useState(false);
  // Completion overlay removed - using confetti instead
  // Controls loading state for next options
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  // Track if we've already initialized to prevent re-initialization loops
  const hasInitializedRef = useRef(false);
  const initialPathIdRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000';

  // --- RESOLVED NODES (overlay what-if overrides) ---
  const resolvedUserNodes = useMemo(
    () => getResolvedNodes(userNodes, whatIfOverrides),
    [userNodes, whatIfOverrides]
  );

  // --- REUSABLE FETCH FUNCTION ---
  const fetchNextOptions = async (currentNode, currentPath) => {
      setIsFetchingOptions(true);
      setNextOptions([]);

      // Add timeout to prevent hanging on slow networks
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

      try {
         const { data: { session: _suggestSession } } = await supabase.auth.getSession();
         const response = await fetch(`${BACKEND_URL}/api/suggest-next-roles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${_suggestSession?.access_token}`,
            },
            body: JSON.stringify({
                current_role: currentNode,
                user_context: formData,
                risk_level: riskLevel,
                history: currentPath,
                roadmap_id: getRoadmapId() // Pass roadmapId for caching
            }),
            signal: controller.signal
         });

         clearTimeout(timeoutId);

         if (!response.ok) throw new Error("AI Request Failed");

         const aiOptions = await response.json();
         setNextOptions(aiOptions);

      } catch (err) {
          clearTimeout(timeoutId);
          if (err.name === 'AbortError') {
              toast.error("Request timed out. Please try again.");
          } else {
              toast.error("Failed to load suggested roles. Please check connection.");
          }
      } finally {
          setIsFetchingOptions(false);
      }
  };

  // --- INITIALIZATION ---
  useEffect(() => {
     // Generate a unique path identifier to detect actual data changes vs re-renders
     const pathId = path?.saved_at || path?.generated_at || JSON.stringify(path?.nodes?.map(n => n.role_id || n.title).slice(0, 2));

     // Skip if we've already initialized with this exact path data
     if (hasInitializedRef.current && initialPathIdRef.current === pathId) {
         return;
     }

     // If we have nodes available
     if (path?.nodes?.length > 0) {
         // Mark as initialized
         hasInitializedRef.current = true;
         initialPathIdRef.current = pathId;

         // FOR STABLE: Check if we have a full path saved
         if (riskLevel === 'stable') {
             // Check if this has a selected_path (user has already built this)
             if (path.selected_path && path.selected_path.length > 0) {
                 // Load the user-built path with ensured years
                 const nodesWithYears = ensureNodeYears(path.selected_path);
                 setUserNodes(nodesWithYears);

                 // CHECK IF LOCKED/COMPLETE
                 // Only freeze if explicitly locked in the data
                 const isLocked = path.locked === true;
                 setIsFrozen(isLocked);

                 if (isLocked) {
                     // Path is complete (shareable card shown in parent)
                 } else {
                     // RESUME BUILDING: Fetch options for the last node (paid users only)
                     if (hasLifetimeAccess) {
                         const lastNode = nodesWithYears[nodesWithYears.length - 1];
                         fetchNextOptions(lastNode, nodesWithYears);
                     }
                 }
             } else {
                 // AI-generated path - allow user to build from scratch
                 // DON'T preselect the first node - show all initial options instead
                 setUserNodes([]);
                 setIsFrozen(false); // Allow building

                 // Show the AI-generated first options directly as choices
                 // Use the path.nodes as initial options for user to pick from
                 const initialOptions = ensureNodeYears(path.nodes.slice(0, 4)); // Show up to 4 initial career options
                 setNextOptions(initialOptions);
             }
         } else {
             // For adventurous/risky - show as frozen (read-only view)
             const nodesWithYears = ensureNodeYears(path.nodes || []);
             setUserNodes(nodesWithYears);
             setIsFrozen(true);
         }
     }
  }, [path, riskLevel, formData]);

  // --- AUTO SAVE PROGRESS ---
  const prevUserNodesRef = useRef([]);
  const isSavingRef = useRef(false);
  const saveTimeoutRef = useRef(null);
  const pendingSaveRef = useRef(null); // Track pending saves for unmount
  const onSetRoadmapRef = useRef(onSetRoadmap); // Keep ref to avoid stale closure
  const riskLevelRef = useRef(riskLevel); // Keep ref for unmount cleanup

  // Keep refs updated
  useEffect(() => {
    onSetRoadmapRef.current = onSetRoadmap;
  }, [onSetRoadmap]);

  useEffect(() => {
    riskLevelRef.current = riskLevel;
  }, [riskLevel]);

  // Save immediately (used for unmount and navigation)
  const saveImmediately = (nodesToSave) => {
    if (onSetRoadmapRef.current && riskLevelRef.current === 'stable' && nodesToSave.length > 0) {
      onSetRoadmapRef.current(nodesToSave, false);
    }
  };

  useEffect(() => {
    // Skip during initial load
    if (!hasInitializedRef.current) return;

    // Check if userNodes changed from what we previously saved
    const currentIds = userNodes.map(n => n.role_id || n.title).join(',');
    const prevIds = prevUserNodesRef.current.map(n => n.role_id || n.title).join(',');
    const isDifferent = currentIds !== prevIds;

    if (isDifferent && userNodes.length > 0) {
        prevUserNodesRef.current = [...userNodes]; // Deep copy to prevent mutation issues

        // Only save if we have nodes, saving function, and it's stable path
        if (onSetRoadmap && riskLevel === 'stable') {
             // Check if it matches the prop we received (avoid save loop)
             const propPath = path?.selected_path || [];
             const propIds = propPath.map(n => n.role_id || n.title).join(',');

             // Only save if it's actually different from what's in the DB/props
             if (currentIds !== propIds) {
                 // Track pending save for unmount
                 pendingSaveRef.current = [...userNodes];

                 // Debounce saves to prevent rapid consecutive saves
                 if (saveTimeoutRef.current) {
                     clearTimeout(saveTimeoutRef.current);
                 }

                 // Skip if already saving to prevent overlapping saves
                 if (isSavingRef.current) {
                     return;
                 }

                 saveTimeoutRef.current = setTimeout(() => {
                     // Double-check we're not already saving
                     if (isSavingRef.current) return;

                     isSavingRef.current = true;

                     // Capture current nodes at save time to prevent stale data
                     const nodesToSave = pendingSaveRef.current || userNodes;

                     // Pass false to indicate this is just an auto-save, NOT final
                     onSetRoadmap(nodesToSave, false);
                     pendingSaveRef.current = null; // Clear pending after save

                     // Reset saving flag after a delay
                     setTimeout(() => {
                         isSavingRef.current = false;
                     }, 1000);
                 }, 500); // 500ms debounce
             }
        }
    }

    return () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
    };
  }, [userNodes, onSetRoadmap, riskLevel, path]);

  // CRITICAL: Save on unmount if there are pending changes
  useEffect(() => {
    return () => {
      // Clear any pending debounce timer
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // If there's a pending save that didn't complete, save immediately
      if (pendingSaveRef.current && pendingSaveRef.current.length > 0) {
        saveImmediately(pendingSaveRef.current);
      }
    };
  }, []); // Empty deps - only runs on unmount

  // Get roadmap ID helper
  const getRoadmapId = () => {
    if (roadmapId) return roadmapId;
    const pathParts = window.location.pathname.split('/');
    const idFromPath = pathParts.find(part => part && part !== 'roadmap' && part && part !== 'roles');
    return idFromPath || localStorage.getItem('currentRoadmapId');
  };

  /**
   * NODE SELECTION (Navigation)
   * Navigates to details, same as before
   */
  const handleNodeClick = async (node, index) => {
    if (!hasLifetimeAccess && index > 0) {
      if (onPaywallRequired) onPaywallRequired();
      return;
    }
    if (node.role_id) {
      const currentRoadmapId = getRoadmapId();
      if (currentRoadmapId) {
        navigate(`/roadmap/${currentRoadmapId}/roles/${node.role_id}`, { 
          state: { 
            roleData: {
              id: node.role_id,
              title: getCleanTitle(node.title),
              description: node.description,
              skills: node.details?.skills || [],
              key_actions: node.details?.actions || [],
              chat: [],
              videos: ['https://www.youtube.com/watch?v=w9Wc7KHspuU']
            }
          } 
        });
      } else {
        setSelectedNode(selectedNode === index ? null : index);
      }
    } else {
      setSelectedNode(selectedNode === index ? null : index);
    }
  };

  /**
   * NODE SWAPPING (Updates existing node)
   * Kept for the "InteractiveRoadmapNode" logic if user wants to swap a LOCKED node
   */
  const handleNodeSwap = (index, newRole) => {
    const newNodes = [...userNodes];
    newNodes[index] = {
        ...newRole,
        year: newNodes[index].year,
        role_id: newRole.role_id || `${newNodes[index].role_id}_swapped`
    };
    setUserNodes(newNodes);
  };

  /**
   * BUILDER: ADD NEXT STEP
   * Triggered when user selects a floating bubble option
   * Now uses AI to generate the next options for higher quality.
   */
  const handleAddNextStep = async (optionNode) => {
      // Gate: free users can only pick their first role (index 0)
      if (!hasLifetimeAccess && userNodes.length >= 1) {
        if (onPaywallRequired) onPaywallRequired();
        return;
      }
      // Calculate the year for this new node (1 year after the last node)
      const lastNode = userNodes[userNodes.length - 1];
      const lastYear = lastNode?.year || new Date().getFullYear();
      const newNodeWithYear = {
          ...optionNode,
          year: lastYear + 1 // Always 1 year increments
      };

      // 1. Lock in the new node
      const newPath = [...userNodes, newNodeWithYear];
      setUserNodes(newPath);

      // 2. Progression Control: Limit to 4 nodes
      if (newPath.length < 4) {
          // Only fetch AI suggestions for paid users (/suggest-next-roles requires payment)
          if (hasLifetimeAccess) {
              fetchNextOptions(newNodeWithYear, newPath);
          } else {
              // Free users: clear options — the paywall prompt renders in their place
              setNextOptions([]);
          }
      } else {
          // End of path construction
          setNextOptions([]);
      }
  };

  // Get soft color based on risk level
  const getSoftColor = (level = riskLevel) => {
    if (color) return color;
    switch(level) {
      case 'stable': return '#91FF4B'; // Neon Green
      case 'adventurous': return '#FFB04B'; // Neon Orange
      case 'risky': return '#FF4B91'; // Neon Pink
      default: return '#9333EA'; // Purple
    }
  };

  const softColor = getSoftColor();

  // --- RENDER HELPERS ---

  if (!path || !path.nodes || path.nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
        <div className="w-12 h-12 mb-3 rounded-full bg-slate-100 flex items-center justify-center text-slate-400">
           {isGenerating ? <Loader2 size={24} className="animate-spin text-purple-600" /> : <TrendingUp size={24} />}
        </div>
        <p className="text-sm font-medium text-slate-500 mb-4">
            {isGenerating ? "Crafting your personalized career steps..." : "Path details are missing."}
        </p>
        {!isGenerating && onRetry && (
            <button 
                onClick={onRetry}
                className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 text-sm font-bold transition-colors flex items-center gap-2"
            >
                <Zap size={16} />
                Generate Path
            </button>
        )}
      </div>
    );
  }

  return (
    <div className="roadmap-visualization">
      {/* 1. INTERACTIVE BUILDER SECTION */}
      <div className="relative-div">
        {/* What-If Pegman - visible when path is frozen/complete */}
        {isFrozen && onWhatIfDrop && hasLifetimeAccess && (
          <WhatIfPegman onDrop={onWhatIfDrop} />
        )}

        {/* What-If Popover */}
        {whatIfActiveNode !== null && whatIfActiveNode !== undefined && resolvedUserNodes[whatIfActiveNode] && (
          <WhatIfPopover
            node={resolvedUserNodes[whatIfActiveNode]}
            nodeIndex={whatIfActiveNode}
            onSubmit={onWhatIfSubmit}
            onClose={onWhatIfClose}
            isLoading={whatIfLoading}
          />
        )}

        <div className="roadmap-nodes">
          {resolvedUserNodes.map((node, index) => (
            <React.Fragment key={`${node.year}-${index}-${node.role_id || ''}`}>
              {/* Animated timeline connector from previous node */}
              {index > 0 && (
                <TimelineConnector
                  softColor={softColor}
                  index={index}
                  isAnimating={!isFrozen}
                />
              )}
              <div className="relative">
                <InteractiveRoadmapNode
                  node={node}
                  index={index}
                  riskLevel={riskLevel}
                  softColor={softColor}
                  isLeft={index % 2 === 0}
                  isSelected={selectedNode === index}
                  onSelect={handleNodeClick}
                  onSwap={handleNodeSwap}
                  onDislike={onDislikeRole}
                  onSaveRole={onSaveRole}
                  isSaved={onSaveRole ? (savedRoles || []).some(r => r.title === (node.title ? node.title.split(/ at /i)[0].trim() : '')) : false}
                  userContext={formData}
                  roadmapId={getRoadmapId()}
                  isOverridden={isNodeOverridden(index, whatIfOverrides)}
                  isOverrideStart={isOverrideStartNode(index, whatIfOverrides)}
                  originalNode={getOriginalNodeAt(index, whatIfOverrides)}
                  onResetOverride={onWhatIfReset ? () => onWhatIfReset(riskLevel) : undefined}
                  onWhatIfClick={onWhatIfDrop}
                  onApplyWithTsenta={onApplyWithTsenta}
                />
                {/* Lock overlay for unpaid users on non-first nodes */}
                {!hasLifetimeAccess && index > 0 && (
                  <button
                    onClick={() => { if (onPaywallRequired) onPaywallRequired(); }}
                    className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl cursor-pointer"
                    style={{ background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(4px)' }}
                    aria-label="Unlock this role"
                  >
                    <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <span className="text-xs font-semibold text-gray-500">Unlock Full Roadmap</span>
                  </button>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* SET ROADMAP BUTTON - appears when user has 4 nodes but hasn't finalized */}
      {!isFrozen && userNodes.length >= 4 && riskLevel === 'stable' && (
        <div className="flex justify-center mt-8 mb-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              // Finalize the path - mark as complete
              setIsFrozen(true);
              if (onSetRoadmap) {
                onSetRoadmap(userNodes, true); // isFinal = true
              }

              toast.success("Roadmap set! Adventurous and Risky paths are now unlocked.");
            }}
            className="flex items-center gap-3 px-8 py-4 bg-black text-white rounded-full font-bold text-lg shadow-xl hover:bg-slate-800 transition-all"
          >
            <CheckCircle className="w-5 h-5" />
            Set My Roadmap
          </motion.button>
        </div>
      )}

      {/* Floating "Next Step" Options Bubbles - moved below timeline, always scrollable */}
      {!isFrozen && userNodes.length < 4 && (
      <div
        className="flex flex-col items-center mt-4 space-y-2 w-full"
      >
        {/* Paywall prompt for free users who have picked their first role */}
        {!hasLifetimeAccess && userNodes.length >= 1 ? (
          <div className="flex flex-col items-center gap-3 w-full">
            <button
              onClick={() => { if (onPaywallRequired) onPaywallRequired(); }}
              className="mt-4 sm:mt-8 flex flex-col items-center justify-center gap-4 px-6 sm:px-8 py-8 bg-gradient-to-b from-white to-slate-50 border-2 rounded-[2rem] shadow-xl cursor-pointer transition-all hover:shadow-2xl hover:-translate-y-1 mx-auto w-[90vw] sm:w-full"
              style={{ borderColor: softColor, maxWidth: 400 }}
            >
              <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mb-1 shadow-inner border border-slate-200">
                <svg className="w-7 h-7 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="text-center space-y-2">
                <p className="font-extrabold text-slate-800 text-lg sm:text-xl tracking-tight">Unlock full roadmap</p>
                <p className="text-sm text-slate-500 font-medium">Get lifetime access to continue building out all 4 steps and more</p>
              </div>
              <span className="mt-3 text-sm font-bold px-6 py-3 rounded-full text-white shadow-md active:scale-95 transition-transform" style={{ backgroundColor: '#007AFF', boxShadow: `0 8px 20px -8px #007AFF` }}>
                Get Access — $7.98
              </span>
            </button>
            {userNodes.length === 1 && (
              <button
                onClick={() => {
                  setUserNodes([]);
                  setNextOptions(ensureNodeYears(path.nodes.slice(0, 4)));
                }}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              >
                ← Try a different starting role
              </button>
            )}
          </div>
        ) : (
        <div className="text-sm font-semibold text-slate-500 animate-pulse flex items-center gap-2">
          {userNodes.length === 0 ? <Flag size={14} /> : <ArrowRight size={14} />}
          {userNodes.length === 0 ? "Scroll to select your starting role..." : "Choose your next step..."}
        </div>
        )}
        
        {(!hasLifetimeAccess && userNodes.length >= 1) ? null : isFetchingOptions ? (
            <div className="w-full py-8 flex flex-col items-center justify-center text-slate-400 gap-2">
                <Loader2 size={24} className="animate-spin text-purple-500" />
                <span className="text-xs font-medium">Analyzing career paths...</span>
            </div>
        ) : (
            <div className="w-full max-w-4xl px-4">
            <div className="flex overflow-x-auto pb-6 pt-2 gap-4 snap-x snap-mandatory scrollbar-hide" style={{ scrollBehavior: 'smooth', maxWidth: '100vw' }}>
                {nextOptions.length > 0 ? (
                    nextOptions.map((opt, i) => (
                    <motion.button
                        key={i}
                        type="button"
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={(e) => { e.preventDefault(); e.currentTarget.blur(); handleAddNextStep(opt); }}
                        className="snap-center shrink-0 bg-white border-2 p-6 rounded-2xl shadow-lg cursor-pointer w-[85vw] sm:w-[400px] max-w-[400px] text-left transition-all relative overflow-hidden group flex flex-col justify-between"
                        style={{ borderColor: softColor, minHeight: opt.companies && opt.companies.length > 0 ? '280px' : '240px', boxShadow: `0 10px 30px -10px ${softColor}40, 0 4px 6px -4px rgba(0,0,0,0.1)` }}
                    >
                        <div className="absolute inset-0 bg-slate-50 opacity-0 group-hover:opacity-100 transition-opacity" />
                        <div className="relative z-10 flex flex-col h-full">
                        <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                            <span className="text-[10px] uppercase font-bold text-purple-600 bg-purple-50 px-2 py-1 rounded tracking-wide ml-auto">
                                {opt.type || 'Next Step'}
                            </span>
                            </div>

                            <h5 className="font-bold text-slate-800 text-lg leading-tight mb-2 pr-2">{getCleanTitle(opt.title)}</h5>

                            <p className="text-sm text-slate-600 leading-normal line-clamp-2 mb-3">{opt.description}</p>

                            {/* Companies Section */}
                            {opt.companies && opt.companies.length > 0 && (
                              <div className="mb-3">
                                <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wide mb-1.5">Where to find this role</p>
                                <div className="flex flex-wrap gap-1">
                                  {opt.companies.slice(0, 3).map((company, idx) => (
                                    <span
                                      key={idx}
                                      className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                      style={{
                                        backgroundColor: `${softColor}15`,
                                        color: softColor,
                                        border: `1px solid ${softColor}30`
                                      }}
                                    >
                                      {company}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                        </div>

                        <div className="mt-auto pt-4 flex items-center justify-between border-t border-slate-100">
                            {opt.salary && (
                            <div className="flex flex-col">
                                <span className="text-[10px] uppercase text-slate-400 font-bold">Est. Salary</span>
                                <span className="text-xs font-bold text-slate-700">{opt.salary}</span>
                            </div>
                            )}
                            <div className="p-2 rounded-full bg-slate-50 text-slate-400 group-hover:bg-purple-100 group-hover:text-purple-600 transition-colors">
                            <ArrowRight size={16} />
                            </div>
                        </div>
                        </div>

                    </motion.button>
                    ))
                ) : (
                    <div className="w-full text-center text-slate-400 text-sm py-4">
                        No suggestions found. Try refreshing.
                    </div>
                )}
            </div>
            </div>
        )}
      </div>
      )}

        {/* Regenerate Roadmap Button (Reset) - appears when stable path is complete */}
        {isFrozen && hasLifetimeAccess && riskLevel === 'stable' && (
             <div className="flex flex-col items-center mt-12 pb-8 gap-3">
                 <p className="text-sm text-slate-500 text-center max-w-md">
                    Want to explore different career options? You can start fresh and build a new roadmap.
                 </p>
                 <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                        // Reset completely - allow user to pick first career again
                        setUserNodes([]);
                        setIsFrozen(false);

                        // Show the initial career options again (from AI-generated path)
                        const initialOptions = ensureNodeYears(path.nodes.slice(0, 4));
                        setNextOptions(initialOptions);

                        // Notify parent to save the reset state
                        if (onSetRoadmap) onSetRoadmap([], false);

                        toast.info("Let's build a new roadmap! Pick your first career step.");
                    }}
                    className="flex items-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 border-2 border-slate-300 rounded-full font-bold text-sm shadow-sm hover:bg-slate-200 hover:border-slate-400 transition-all"
                 >
                    <RefreshCw className="w-4 h-4" />
                    Regenerate Roadmap
                 </motion.button>
             </div>
        )}

        <div ref={bottomRef} />
    </div>
  );
};

export default RoadmapVisualization;
